<template>
  <b-form-group :label='`${config.label}:`'
                :label-for="config.id"
                content-cols-sm="4"
                content-cols-md="4"
                content-cols-xxl="4"
                label-cols-sm="4"
                label-cols-md="4"
                label-cols-xxl="4"
                label-class="text-nowrap"
  >
    <template v-slot:description>
      <div v-if="config.description">{{config.description}}</div>
      <div v-if="defaultSettingValue">Default: {{defaultSettingValue}}</div>
    </template>
    <b-input-group :prepend="config.prepend" :append="config.append">
        <b-form-input :id="config.id"
                      v-model="settingValue"
                      :placeholder="config.placeholder || ''"
                      @input="validate"
                      :state="validation.valid"
        />
    </b-input-group>
    <!-- This will only be shown if the preceding input has an invalid state -->
    <b-form-invalid-feedback :state="validation.valid">
      <div v-for="(error, index) in validation.errors" :key="`${config.id}-error-${index}`">
        {{error}}
      </div>
    </b-form-invalid-feedback>
  </b-form-group>

</template>

<script lang="ts">
import {defineComponent} from "vue";
import {mapActions, mapGetters, mapMutations} from "vuex";
import objectPath from "object-path";
import {isFunction} from "lodash";


type ComponentData = {
  validation: {
    valid: boolean|null,
    errors: [],
  }
}

export default defineComponent({
  name: "Setting",

  props: {
    config: {
      type: Object,
      required: true,
    },
  },

  data(): ComponentData {
    return {
      validation: {
        valid: null,
        errors: [],
      }
    }
  },

  watch: {
    'selectedShop.id'() {
      this.validation.valid = null;
      this.validation.errors = [];
    }
  },

  computed: {
    ...mapGetters({
      selectedShop: 'shopify/shops/selected',
      selectedShopUpdate: 'shopify/shops/selectedUpdate',
      defaultSettings: 'shopify/shops/defaultSettings',
    }),

    defaultSettingValue(): undefined|null|number|string {
      if(!this.defaultSettings) return null;
      return objectPath.get(this.defaultSettings, this.config.path);
    },

    settingValue:  {
      get(): null|number|string {
        let updatedSettingValue = undefined;
        if(this.selectedShopUpdate) updatedSettingValue = objectPath.get(this.selectedShopUpdate, this.config.path);
        const settingValue = (updatedSettingValue !== undefined)
          ? updatedSettingValue
          : (objectPath.get(this.selectedShop?.settings ?? {}, this.config.path) ?? null);

        return ('normalize' in this.config && 'onGet' in this.config.normalize && isFunction(this.config.normalize.onGet))
          ? this.config.normalize.onGet(settingValue)
          : settingValue;
      },
      set(value) {
        const normalizedValue = ('normalize' in this.config && 'onSet' in this.config.normalize && isFunction(this.config.normalize.onSet))
          ? this.config.normalize.onSet(value)
          : value;
        const settings = {};
        objectPath.set(settings, this.config.path, normalizedValue);

        this.updateSelected({settings});
      }
    },
  },

  methods: {
    ...mapMutations({
      updateSelected: 'shopify/shops/updateSelected',
      updateSelectedValidation: 'shopify/shops/updateSelectedValidation',
    }),

    ...mapActions({
      updateSettings: 'shopify/shops/updateSettings'
    }),

    validate(value) {
      if(!this.config.validationRules) {
        this.validation.valid = (value === null) ? null : true;
        this.validation.errors = [];
        return;
      }

      this.config.validationRules.validate((value === '') ? null : value)
        .then((value) => {
          this.validation.valid = (value === null) ? null : true;
          this.validation.errors = [];
        })
        .catch((err) => {
          this.validation.valid = false;
          this.validation.errors = err.errors;
        })
        .finally(() => {
          this.updateSelectedValidation({
            field: `settings.${this.config.path}`,
            valid: this.validation.valid,
          });
        });
    },
  }
});
</script>

<style scoped>

</style>